<template>
  <div>
    <h1>Manage Buckets</h1>
  
    <b-button v-if="isAdmin()" variant="primary" style="margin-bottom:20px;" v-b-modal.modal-crud>New Bucket</b-button>

    <b-modal id="modal-crud" hide-header hide-footer>
      <h2>{{formTitle}}</h2>
      <hr/>
      <form ref="form" @submit.prevent="save">
        <b-form-group 
            label="Business Friendly Bucket Name" label-for="biz-name-input"
            invalid-feedback="Business Friendly Bucket Name is required">
            <b-form-input id="biz-name-input" 
              v-model="$v.editedItem.businessName.$model" 
              :state="$v.editedItem.businessName.$dirty ? !$v.editedItem.businessName.$error : null"
              aria-describedby="input-1-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-1-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="biz-name-input-help">Required.</b-form-text> 
        </b-form-group>
          
        <b-form-group 
            label="AWS Bucket Name" label-for="aws-name-input" 
            invalid-feedback="AWS Bucket Name is required">
            <b-form-input id="aws-name-input" 
              v-model="$v.editedItem.bucketName.$model" 
              :state="$v.editedItem.bucketName.$dirty ? !$v.editedItem.bucketName.$error : null"
              aria-describedby="input-2-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-2-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="aws-name-input-help">Required.</b-form-text> 
        </b-form-group>  

        <b-form-group 
            label="AC2Code" label-for="ac2code-input" 
            invalid-feedback="AC2Code is required">
            <b-form-input id="ac2code-input" 
              v-model="$v.editedItem.ac2Code.$model" 
              :state="$v.editedItem.ac2Code.$dirty ? !$v.editedItem.ac2Code.$error : null"
              aria-describedby="input-3-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-3-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="ac2code-input-help">Required.</b-form-text> 
        </b-form-group> 

         <b-form-group
                  label="Bucket Content Type" label-for="type-select"
                  invalid-feedback="Bucket Content Type is required">
            <b-form-select id="type-select"
              v-model="$v.editedItem.contentType.$model"
              value-field=""
              :state="$v.editedItem.contentType.$dirty ? !$v.editedItem.contentType.$error : null"
              aria-describedby="select-4-live-feedback"
              required>
              <template v-slot:first>
                <b-form-select-option :value="null" disabled selected>-- Select --</b-form-select-option>
              </template>
              <option v-for="t in contentTypes" :value="t.code" :key="t.code">{{t.label}}</option>
            </b-form-select>
            <b-form-invalid-feedback id="select-4-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>
            <b-form-text id="type-select-help">Required.</b-form-text>
          </b-form-group>

        <b-form-group 
            label="Requestor / Owner" label-for="owner-input" 
            invalid-feedback="Requestor / Owner is required">
            <b-form-input id="owner-input" 
              v-model="$v.editedItem.ownership.$model" 
              :state="$v.editedItem.ownership.$dirty ? !$v.editedItem.ownership.$error : null"
              aria-describedby="input-5-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-5-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="owner-input-help">Required.</b-form-text> 
        </b-form-group> 

          <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
        </form>
    </b-modal>

    <b-table hover small responsive show-empty
      :fields="fields"
      :items="buckets"
      :busy="isLoading">
      <template v-slot:cell(actions)="row">
        <b-button size="sm" @click="editItem(row.item)" class="mr-1">
          edit
        </b-button>
        <b-button size="sm" @click="viewUsers(row.item)" class="mr-1">
            users
        </b-button>
        <b-button size="sm" @click="deleteItem(row.item)" class="mr-1">
            delete
        </b-button>
      </template>
      
      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>

      <template v-slot:empty="scope">
        <p>No buckets found.</p>
      </template>

      <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"></b-spinner>
          <strong>Loading, Please Wait...</strong>
      </div>        

    </b-table>
  </div>
</template>

<script>
  import config from '@/config'
  import axios from 'axios';
  import axiosRetry from 'axios-retry';
  import { validationMixin } from 'vuelidate'
  import { required, minLength } from 'vuelidate/lib/validators'

  axiosRetry(axios, { retries: 3,
                      retryDelay: axiosRetry.exponentialDelay  });

  export default {
    mixins: [validationMixin],
    data: () => ({
      fields: [
        {key: 'businessName', sortable: true, label: 'Name'},
        {key: 'bucketName', sortable: true, label: 'Bucket Name'},
        {key: 'ac2Code', sortable: true, label: 'AC2Code'},
        {key: 'contentType', sortable: true, label: 'Type'},
        {key: 'actions', label: 'Actions'}
      ],
      isLoading: false,
      buckets: [],
      editedIndex: -1,
      editedItem: {
        businessName: null,
        bucketName: null,
        ac2Code: null,
        contentType: null,
        ownership: null,
      },
      defaultItem: {
        businessName: null,
        bucketName: null,
        ac2Code: null,
        contentType: null,
        ownership: null,
      },
      contentTypes: [
          {"code": "01", "label": "File Share"},
          {"code": "02", "label": "Audio Transcribe"},
          {"code": "03", "label": "Image Object Detection"},
          {"code": "04", "label": "Video Transcribe"},
      ],
    }),

    validations: {
      editedItem: {
        businessName: { required, },
        bucketName: { required, },
        ac2Code: { required, },
        contentType: { required, },
        ownership: { required, },
      }
    },

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? 'New Bucket' : 'Edit Bucket'
      }
    },

    watch: {
      buckets() {
        this.isLoading = false
      }
    },

    created () {
      this.initialize()
    },

    methods: {
      isAdmin() {
          return this.$keycloak.hasResourceRole('admin')
      },
      isBucketAdmin() {
          return this.$keycloak.hasResourceRole('bucket-admin')
      },
      fetchBuckets() {
          this.isLoading = true;
          if (this.isAdmin()) {
              axios.get(config.CLOUDDRIVE_API_ENDPOINT + 'buckets/all')
                  .then(response => {
                      this.buckets = response.data;
                      this.isLoading = false;
                  })
          } else if(this.isBucketAdmin()) {
              axios.get(config.CLOUDDRIVE_API_ENDPOINT + 'buckets/admin/')
                  .then(response => {
                      this.buckets = response.data;
                      this.isLoading = false;
                  })
          }

      },

      initialize () {
        this.fetchBuckets()
      },

      editItem (item) {
        this.editedIndex = this.buckets.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.$bvModal.show('modal-crud')
      },

      deleteItem (item) {
        var outcome = ''
        this.$bvModal.msgBoxConfirm(
           `Please confirm that you want to delete the bucket ${item.businessName}.`, {
          title: 'Please Confirm',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'YES',
          cancelTitle: 'NO',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        })
          .then(value => {
            outcome = value
            if(value) {
                axios.delete(config.CLOUDDRIVE_API_ENDPOINT + 'bucket/' + item.id)
                    .then(response => {
                        this.fetchBuckets()
                    })
            } else {
                this.$bvModal.hide
            }
          })
      },

      close () {
        this.$bvModal.hide('modal-crud')
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
          this.fetchBuckets()
        }, 300)
      },

      save () {
        if (this.editedIndex > -1) {
            axios.put(config.CLOUDDRIVE_API_ENDPOINT + 'bucket', this.editedItem)
            .then(response => { 
              this.fetchBuckets()
                this.$bvToast.toast('Bucket Updated', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                }) 
            });
          
        } else {
            axios.post(config.CLOUDDRIVE_API_ENDPOINT + 'bucket', this.editedItem)
            .then(response => { 
              this.fetchBuckets()
                this.$bvToast.toast('Bucket Saved', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                }) 
            });
          
        }
        this.close()
      },

      viewUsers(item) {
        this.$router.push({ name: 'users', params: { id: item.id, bucketName: item.businessName } })
      },
    }
  }
</script>